import React, { useEffect, useRef, useState, useCallback } from 'react';

import { createChart } from 'lightweight-charts';
import { useStockData } from '../../../hooks/useStockData';
import './StockChart.css';

console.log('🔍 Imported createChart:', createChart);

const StockChart = ({ symbol, period = '1y', onChartClick, showControls = true, enableInteractions = true, showShortMAs = true, showLongMA = true, showMaNote = false, showRefreshInfo = false, showTimeframes = false }) => {
  const chartContainerRef = useRef();
  const chartRef = useRef();
  const candleSeriesRef = useRef();
  const lastMarkerRef = useRef(null);
  const lastPriceLineRef = useRef(null);
  const [containerMounted, setContainerMounted] = useState(false);
  const [usdInr, setUsdInr] = useState(83.0); // fallback
  const [timeframe, setTimeframe] = useState(period || '1y');
  const legendRef = useRef(null);

  // Compact number formatter K/M/B/T
  const compact = useCallback((num) => {
    if (num == null || isNaN(num)) return 'N/A';
    const abs = Math.abs(num);
    if (abs >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
    if (abs >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return Number(num).toFixed(2);
  }, []);

  // Date formatter for historical data
  const formatDate = useCallback((timestamp) => {
    try {
      if (!timestamp) return '—';
      
      let date;
      if (typeof timestamp === 'number') {
        // Check if it's already in milliseconds or seconds
        if (timestamp > 2000000000) {
          // Already in milliseconds
          date = new Date(timestamp);
        } else {
          // In seconds, convert to milliseconds
          date = new Date(timestamp * 1000);
        }
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (timestamp && typeof timestamp === 'object' && typeof timestamp.year === 'number') {
        // Business day object from Lightweight Charts
        date = new Date(Date.UTC(timestamp.year, (timestamp.month || 1) - 1, timestamp.day || 1));
      } else {
        return '—';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '—';
      }
      
      // Format as MM/DD/YYYY
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return '—';
    }
  }, []);

  // Use the custom stock data hook
  const {
    stockData,
    isLoading,
    error,
    lastUpdated,
    fetchStockData,
    refreshStockData
  } = useStockData(symbol, {
    autoFetch: true,
    dataTypes: ['history', 'price']
  });

  console.log('🎨 StockChart render - symbol:', symbol, 'loading:', isLoading, 'error:', error, 'containerMounted:', containerMounted);

  const setContainerRef = useCallback((node) => {
    console.log('🔧 setContainerRef called with node:', node);
    console.log('🔧 node type:', typeof node);
    console.log('🔧 node instanceof HTMLElement:', node instanceof HTMLElement);
    
    if (node) {
      chartContainerRef.current = node;
      setContainerMounted(true);
      console.log('✅ Container ref set successfully');
      console.log('📏 Container dimensions:', {
        width: node.clientWidth,
        height: node.clientHeight
      });
      console.log('🔧 Setting containerMounted to true');
    } else {
      console.log('❌ setContainerRef called with null node');
      chartContainerRef.current = null;
      setContainerMounted(false);
      console.log('🔧 Setting containerMounted to false');
    }
  }, []);

  // Fetch USD->INR rate on mount
  useEffect(() => {
    let active = true;
    const fetchFx = async () => {
      try {
        const resp = await fetch('https://api.exchangerate.host/latest?base=USD&symbols=INR');
        const data = await resp.json();
        const rate = data?.rates?.INR;
        if (active && typeof rate === 'number') setUsdInr(rate);
      } catch (_) {
        // keep fallback
      }
    };
    fetchFx();
    return () => { active = false; };
  }, []);

  // Debug useEffect to track containerMounted changes
  useEffect(() => {
    console.log(' containerMounted changed to:', containerMounted);
  }, [containerMounted]);

  // Debug useEffect to check if container exists in DOM
  useEffect(() => {
    console.log(' Checking DOM for chart-container...');
    const container = document.querySelector('[data-debug="chart-container"]');
    console.log(' Found container in DOM:', container);
    if (container) {
      console.log(' Container dimensions:', {
        width: container.clientWidth,
        height: container.clientHeight,
        offsetWidth: container.offsetWidth,
        offsetHeight: container.offsetHeight
      });
    }
  }, []);

  useEffect(() => {
    console.log('=== useEffect triggered ===');
    console.log('symbol:', symbol);
    console.log('containerMounted:', containerMounted);
    console.log('chartContainerRef.current:', chartContainerRef.current);
    console.log('chartContainerRef.current type:', typeof chartContainerRef.current);
    console.log('stockData:', stockData);
    console.log('timeframe:', timeframe);
    
    if (!symbol) {
      console.log('❌ Missing symbol, returning early');
      return;
    }

    if (!containerMounted) {
      console.log('❌ Container not mounted yet, returning early');
      return;
    }

    if (!chartContainerRef.current) {
      console.log('❌ chartContainerRef.current is null/undefined, returning early');
      return;
    }

    if (!stockData || !stockData.history?.data) {
      console.log('❌ No stock data available yet, returning early');
      console.log('stockData:', stockData);
      console.log('stockData.history:', stockData?.history);
      console.log('stockData.history.data:', stockData?.history?.data);
      return;
    }

    console.log('✅ All conditions met, proceeding with chart creation');

    // Clean up existing chart first
    if (chartRef.current) {
      console.log('🧹 Cleaning up existing chart');
      try {
        chartRef.current.remove();
      } catch (err) {
        console.log('⚠️ Error removing existing chart:', err);
      }
      chartRef.current = null;
    }

    try {
      const chartData = stockData.history.data;
      console.log('📊 Chart data from API:', chartData);
      
      if (!chartData || chartData.length === 0) {
        console.log('❌ No chart data available');
        return;
      }
      
      console.log('🎯 Creating chart with container:', chartContainerRef.current);
      console.log('📏 Container dimensions:', {
        width: chartContainerRef.current.clientWidth,
        height: chartContainerRef.current.clientHeight
      });

      // Create new chart
      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: chartContainerRef.current.clientHeight,
        layout: {
          background: { color: '#ffffff' },
          textColor: '#1f2937',
        },
        grid: {
          vertLines: { color: '#f3f4f6' },
          horzLines: { color: '#f3f4f6' },
        },
        crosshair: {
          mode: 1,
        },
        watermark: {
          visible: false,
        },
        rightPriceScale: {
          borderColor: '#e5e7eb',
          scaleMargins: { top: 0.1, bottom: 0.15 },
        },
        timeScale: {
          borderColor: '#e5e7eb',
          timeVisible: true,
          secondsVisible: false,
        },
      });

      chartRef.current = chart;

      // Add candlestick series (show INR symbol on scale)
      const candlestickSeries = chart.addCandlestickSeries({
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: false,
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350',
        priceFormat: {
          type: 'custom',
          formatter: (price) => `₹${compact(Number(price))}`,
          minMove: 0.01,
        },
        priceLineVisible: true,
        lastPriceAnimation: 1,
      });
      candleSeriesRef.current = candlestickSeries;

      // Add volume series
      const volumeSeries = chart.addHistogramSeries({
        color: '#26a69a',
        priceFormat: {
          type: 'volume',
        },
        priceScaleId: '',
        scaleMargins: {
          top: 0.8,
          bottom: 0,
        },
      });

      // Determine currency and convert only if USD
      const currency = stockData?.price?.currency || 'USD';
      const toInr = (v) => (v == null ? v : (currency === 'USD' ? v * usdInr : v));

      // Prepare data for chart (convert OHLC to INR values)
      const candlestickData = chartData.map(item => ({
        time: item.date,
        open: toInr(item.open),
        high: toInr(item.high),
        low: toInr(item.low),
        close: toInr(item.close),
      }));

      const volumeData = chartData.map(item => ({
        time: item.date,
        value: item.volume,
        color: item.close >= item.open ? '#26a69a' : '#ef5350',
      }));

      console.log('📊 Prepared candlestick data:', candlestickData.slice(0, 5));
      console.log('📊 Prepared volume data:', volumeData.slice(0, 5));

      // Add simple moving averages (20, 50, 200)
      const sma = (data, window) => {
        const out = [];
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          sum += data[i].close;
          if (i >= window) sum -= data[i - window].close;
          if (i >= window - 1) {
            out.push({ time: data[i].time, value: sum / window });
          }
        }
        return out;
      };

      let sma20 = null;
      let sma50 = null;
      let sma200 = null;

      // Set base data
      candlestickSeries.setData(candlestickData);

      if (showLongMA) {
        sma200 = chart.addLineSeries({ color: '#f59e0b', lineWidth: 2, priceLineVisible: false });
        const sma200Data = sma(candlestickData, 200);
        sma200.setData(sma200Data);
      }

      if (showShortMAs) {
        sma20 = chart.addLineSeries({ color: '#3b82f6', lineWidth: 2, priceLineVisible: false });
        sma50 = chart.addLineSeries({ color: '#8b5cf6', lineWidth: 2, priceLineVisible: false });
        const sma20Data = sma(candlestickData, 20);
        const sma50Data = sma(candlestickData, 50);
        sma20.setData(sma20Data);
        sma50.setData(sma50Data);
      }
      volumeSeries.setData(volumeData);

      // Fit content
      chart.timeScale().fitContent();

      console.log('✅ Chart created successfully');

      // Hover legend
      const updateLegend = (param) => {
        if (!legendRef.current) return;
        if (!param || !param.time || !param.seriesPrices) {
          legendRef.current.innerHTML = '';
          return;
        }
        const time = param.time;
        const candle = param.seriesPrices.get(candlestickSeries);
        if (!candle) return;
        const v20 = sma20 ? param.seriesPrices.get(sma20) : null;
        const v50 = sma50 ? param.seriesPrices.get(sma50) : null;
        const v200 = sma200 ? param.seriesPrices.get(sma200) : null;
        const fmt = (v) => (v == null ? '—' : `₹${compact(Number(v))}`);
        
        let maLine = '';
        if (showShortMAs && showLongMA) {
          maLine = `MA20: ${fmt(v20)} · MA50: ${fmt(v50)} · MA200: ${fmt(v200)}`;
        } else if (showShortMAs) {
          maLine = `MA20: ${fmt(v20)} · MA50: ${fmt(v50)}`;
        } else if (showLongMA) {
          maLine = `MA200: ${fmt(v200)}`;
        }
        
        legendRef.current.innerHTML = `
          <div><strong>${new Date(time * 1000).toLocaleDateString()}</strong></div>
          <div>O: ${fmt(candle.open)} H: ${fmt(candle.high)} L: ${fmt(candle.low)} C: ${fmt(candle.close)}</div>
          ${maLine ? `<div>${maLine}</div>` : ''}
        `;
      };
      chart.subscribeCrosshairMove(updateLegend);

      // Click handler - also compute clicked price and OHLC at that time
      if (enableInteractions && onChartClick) {
        chart.subscribeClick((param) => {
          try {
            if (!param) return onChartClick({ type: 'price', symbol, event: 'click', detail: null });

            let clickedPrice = null;
            try {
              if (param.point && candleSeriesRef.current && candleSeriesRef.current.priceScale) {
                const ps = candleSeriesRef.current.priceScale();
                if (ps && typeof ps.coordinateToPrice === 'function') {
                  const p = ps.coordinateToPrice(param.point.y);
                  if (typeof p === 'number' && !Number.isNaN(p)) clickedPrice = p;
                }
              }
            } catch (_) {}

            // Find OHLC for the clicked bar (if time is present)
            let ohlc = null;
            let history = []; // Initialize as empty array instead of null
            
            if (param.time && Array.isArray(candlestickData)) {
              const clickedIndex = candlestickData.findIndex(d => d.time === param.time);
              if (clickedIndex !== -1) {
                const c = candlestickData[clickedIndex];
                // Get volume data for the clicked point
                const volumeItem = chartData.find(item => item.date === param.time);
                const volume = volumeItem ? volumeItem.volume : 0;
                ohlc = { o: c.open, h: c.high, l: c.low, c: c.close, v: volume };
                
                // Get historical OHLCV data around the clicked point (50 bars on each side)
                const startIndex = Math.max(0, clickedIndex - 50);
                const endIndex = Math.min(candlestickData.length, clickedIndex + 50);
                const volumeData = chartData.map(item => ({
                  time: item.date,
                  open: toInr(item.open),
                  high: toInr(item.high),
                  low: toInr(item.low),
                  close: toInr(item.close),
                  volume: item.volume
                }));
                
                history = volumeData.slice(startIndex, endIndex).map(item => ({
                  time: formatDate(item.time),
                  o: item.open,
                  h: item.high,
                  l: item.low,
                  c: item.close,
                  v: item.volume
                }));
              }
            }
            
            // Ensure history is always an array
            if (!Array.isArray(history)) {
              console.warn('⚠️ History is not an array, setting to empty array');
              history = [];
            }

            const payload = {
              type: 'price',
              symbol,
              event: 'click',
              time: param.time ?? null,
              logical: param.logical ?? null,
              point: param.point ? { x: param.point.x, y: param.point.y } : null,
              price: clickedPrice,
              ohlc,
              history,
            };
            onChartClick(payload);
          } catch (_) {}
        });

        // Also drop a visual marker on the candlestick series and a price line at exact click
        chart.subscribeClick((param) => {
          try {
            if (!param || !param.time || !candleSeriesRef.current) return;
            const marker = {
              time: param.time,
              position: 'inBar',
              color: '#000000',
              shape: 'circle',
              text: '',
            };
            lastMarkerRef.current = marker;
            candleSeriesRef.current.setMarkers([marker]);

            // Create/replace a price line at the clicked price if possible
            if (param.point && candleSeriesRef.current.priceScale) {
              const ps = candleSeriesRef.current.priceScale();
              if (ps && typeof ps.coordinateToPrice === 'function') {
                const price = ps.coordinateToPrice(param.point.y);
                if (typeof price === 'number' && !Number.isNaN(price)) {
                  // remove previous price line
                  if (lastPriceLineRef.current && typeof candleSeriesRef.current.removePriceLine === 'function') {
                    try { candleSeriesRef.current.removePriceLine(lastPriceLineRef.current); } catch (_) {}
                  }
                  const line = candleSeriesRef.current.createPriceLine({
                    price,
                    color: '#000000',
                    lineWidth: 1,
                    lineStyle: 0,
                    axisLabelVisible: true,
                    title: ''
                  });
                  lastPriceLineRef.current = line;
                }
              }
            }
          } catch (_) {}
        });
      }

    } catch (err) {
      console.error('❌ Error creating chart:', err);
    }

    // Cleanup function for this effect
    return () => {
      if (chartRef.current) {
        console.log('🧹 Cleaning up chart on effect cleanup');
        try {
          chartRef.current.remove();
        } catch (err) {
          console.log('⚠️ Error removing chart during cleanup:', err);
        }
        chartRef.current = null;
      }
    };

  }, [symbol, containerMounted, stockData, usdInr, timeframe]);

  // Handle resize
  const handleResize = useCallback(() => {
    if (chartRef.current && chartContainerRef.current) {
      try {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        });
      } catch (err) {
        console.log('⚠️ Error resizing chart:', err);
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.remove();
      }
    };
  }, []);

  const handleRefresh = () => {
    if (symbol) {
      refreshStockData(symbol, ['history', 'price'], timeframe);
    }
  };

  // Refresh when timeframe changes
  useEffect(() => {
    handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, symbol]);

  // Listen for marker clearing events
  useEffect(() => {
    const handleClearMarkers = () => {
      if (candleSeriesRef.current && typeof candleSeriesRef.current.setMarkers === 'function') {
        try {
          candleSeriesRef.current.setMarkers([]);
        } catch (_) {}
      }
      // Clear price line
      if (lastPriceLineRef.current && candleSeriesRef.current && typeof candleSeriesRef.current.removePriceLine === 'function') {
        try {
          candleSeriesRef.current.removePriceLine(lastPriceLineRef.current);
        } catch (_) {}
      }
      lastPriceLineRef.current = null;
    };
    window.addEventListener('clearChartMarkers', handleClearMarkers);
    return () => window.removeEventListener('clearChartMarkers', handleClearMarkers);
  }, []);

  if (!symbol) {
    return (
      <div className="stock-chart-container">
        <div className="chart-placeholder">
          <p>Please select a stock symbol to view the chart</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stock-chart-container">
      <div className="chart-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h3 style={{ margin: 0 }}>{symbol} Stock Chart</h3>
        {showControls ? (
          <div className="chart-controls" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="timeframe-buttons" style={{ display: 'flex', gap: 8 }}>
              {['1mo','3mo','6mo','1y','5y'].map(tf => (
                <button
                  key={tf}
                  onClick={() => setTimeframe(tf)}
                  className={`tf-button ${timeframe === tf ? 'active' : ''}`}
                  disabled={isLoading && timeframe === tf}
                >
                  {tf.toUpperCase()}
                </button>
              ))}
            </div>
            <button 
              onClick={handleRefresh}
              disabled={isLoading}
              className="refresh-button"
            >
              {isLoading ? '🔄' : '🔄'} Refresh
            </button>
            {lastUpdated && (
              <span className="last-updated" style={{ fontSize: 12, color: '#6b7280' }}>
                Last updated: {new Date(lastUpdated).toLocaleTimeString()}
              </span>
            )}
          </div>
        ) : (
          (showRefreshInfo || showTimeframes) && (
            <div className="chart-controls" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {showTimeframes && (
                <div className="timeframe-buttons" style={{ display: 'flex', gap: 8 }}>
                  {['1mo','3mo','6mo','1y','6y'].map(tf => (
                    <button
                      key={tf}
                      onClick={() => setTimeframe(tf)}
                      className={`tf-button ${timeframe === tf ? 'active' : ''}`}
                      disabled={isLoading && timeframe === tf}
                    >
                      {tf.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
              {showRefreshInfo && (
                <>
                  <button 
                    onClick={handleRefresh}
                    disabled={isLoading}
                    className="refresh-button"
                  >
                    {isLoading ? '🔄' : '🔄'} Refresh
                  </button>
                  {lastUpdated && (
                    <span className="last-updated" style={{ fontSize: 12, color: '#6b7280' }}>
                      Last updated: {new Date(lastUpdated).toLocaleTimeString()}
                    </span>
                  )}
                </>
              )}
            </div>
          )
        )}
      </div>
      {showMaNote && (
        <div style={{
          marginTop: 4,
          marginBottom: 6,
          fontSize: 12,
          color: '#6b7280'
        }}>
          {showShortMAs && showLongMA && 'Blue = MA20 (short-term trend), Purple = MA50 (medium-term trend), Yellow = MA200 (long-term trend)'}
          {showShortMAs && !showLongMA && 'Blue = MA20 (short-term trend), Purple = MA50 (medium-term trend)'}
          {!showShortMAs && showLongMA && 'Yellow = MA200 (long-term trend)'}
        </div>
      )}
      
      {error && (
        <div className="chart-error">
          <p>Error loading chart data: {error}</p>
          <button onClick={handleRefresh}>Retry</button>
        </div>
      )}
      
      {isLoading && !stockData && (
        <div className="chart-loading">
          <p>Loading chart data...</p>
        </div>
      )}
      
      <div style={{ position: 'relative' }}>
        <div 
          ref={setContainerRef}
          className="chart-container"
          data-debug="chart-container"
        />
        {showControls && (
          <div
            ref={legendRef}
            className="chart-legend"
            style={{
              position: 'absolute',
              left: 12,
              top: 8,
              background: 'rgba(255,255,255,0.85)',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              padding: '6px 8px',
              fontSize: 12,
              color: '#111827',
              pointerEvents: 'none'
            }}
          />
        )}
      </div>
      
      {stockData && stockData.price && (
        <div className="price-info">
          <div className="current-price">
            <span className="price">
              {(() => {
                const p = stockData.price.price;
                const c = stockData.price.currency || 'USD';
                if (p == null) return 'N/A';
                const inr = c === 'USD' ? p * usdInr : p;
                const lhs = `₹${compact(inr)}`;
                const rhs = c === 'USD' ? ` (USD $${compact(p)})` : '';
                return lhs + rhs;
              })()}
            </span>
            <span className="currency"></span>
          </div>
          <div className="price-details">
            <span>Volume: {stockData.price.volume != null ? compact(stockData.price.volume) : 'N/A'}</span>
            <span>
              {(() => {
                const mc = stockData.price.market_cap;
                const c = stockData.price.currency || 'USD';
                if (mc == null) return 'Market Cap: N/A';
                const inr = c === 'USD' ? mc * usdInr : mc;
                const lhs = `Market Cap: ₹${compact(inr)}`;
                const rhs = c === 'USD' ? ` (USD $${compact(mc)})` : '';
                return lhs + rhs;
              })()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockChart;